<template>
  <section class="max-w-2xl mx-auto py-32">
    <h1
      class="text-center mb-7 text-4xl text-(--main-color-600) dark:text-(--main-color-400) font-semibold"
    >
      Connexion
    </h1>

    <div class="border border-gray-400 dark:border-gray-100 rounded-lg">
      <div class="space-y-5 login-form p-5">
        <UForm
          :schema="schema"
          :state="state"
          class="space-y-4 mb-4"
          @submit="onSubmit"
        >
          <UFormField label="Email" name="email">
            <UInput
              v-model="state.email"
              class="w-full"
              color="main"
              icon="i-lucide-mail"
              placeholder="Email"
              size="lg"
              type="email"
            />
          </UFormField>
          <UFormField label="Mot de passe" name="password">
            <UInput
              v-model="state.password"
              class="w-full"
              color="main"
              icon="i-lucide-lock"
              placeholder="Mot de passe"
              size="lg"
              type="password"
            />
          </UFormField>
          <div class="my-3 flex flex-col justify-center">
            <UButton
              :disabled="pending"
              :loading="pending"
              block
              class="text-black dark:text-white"
              color="main"
              label="Se connecter"
              size="lg"
              type="submit"
            />
          </div>
        </UForm>
        <USeparator color="main" label="OU" size="sm" />
        <div class="subscribe mt-3">
          <div class="text-center">Pas encore client chez nous ?</div>

          <div class="space-y-4 flex flex-col justify-center mt-4">
            <UButton
              block
              class="text-black dark:text-white"
              color="main"
              label="S'inscrire"
              size="lg"
              to="/register"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { z } from "zod";
import type { FormSubmitEvent } from "#ui/types";
import { useAuthStore } from "~/stores/auth";
import type { Login } from "~/models/login";

const { $api } = useNuxtApp();
const { successToast, errorToast } = useToastMessages();

const route = useRoute();
const toast = useToast();
const router = useRouter();
const pending = ref(false);
const authStore = useAuthStore();

const schema = z.object({
  email: z.string({ message: "Champs vide" }).email("Email invalide"),
  password: z
    .string({ message: "Champs vide" })
    .min(12, "Le mot de passe doit contenir au minimum 12 caracteres"),
});

type Schema = z.output<typeof schema>;

const state = reactive({
  email: "",
  password: "",
});

async function onSubmit(event: FormSubmitEvent<Schema>) {
  pending.value = true;

  try {
    const response: Login = await $api.post(
      "/user/login",
      {
        login: event.data.email,
        password: event.data.password,
      },
      "include"
    );
    console.log(response);

    if (response.token) {
      authStore.login(event.data.email, response.token);
      successToast("Connexion reussi", "Vous etes connecter avec success");
      state.email = "";
      state.password = "";
      if (response.isConfirmed) await router.push("/");
      else {
        const expirationDate = new Date(response.codeExpiration);
        const now = new Date();
        if (expirationDate < now) {
          try {
            await $api.post("/user/resend-email-validation", {}, "include");
            successToast(
              "Renouvellement du code de confirmation",
              "Un nouveau code de confirmation a ete envoyer a votre email, veuillez le saisir"
            );
          } catch (error) {
            console.log(error);
            errorToast(
              "Erreur de renouvellement du code de confirmation",
              "Impossible de renouveler le code de confirmation, veuillez contacter l'administrateur du site"
            );
          }
        } else {
          successToast(
            "Activation du compte",
            "Veuillez activer votre compte avec le code recus par mail"
          );
        }
        await router.push("/validate-user");
      }
    }
  } catch {
    errorToast("erreur de connexion", "Email ou mot de passe incorrect");
  } finally {
    pending.value = false;
  }
}

onBeforeMount(async () => {
  if (route.query.showAlert === "true") {
    toast.add({
      title: "Inscription reussi",
      description:
        "Veuillez vous connecter et activer votre compte avec le code recus par mail",
      icon: "i-heroicons-check-solid",
      duration: 5000,
    });
  }
});

definePageMeta({
  middleware: "auth",
});
</script>
